import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import {
  DeleteFacility,
  GetFacilityById,
  UpdateFacility,
} from "../../../hooks/useFacilityData";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";

const AVATAR_GRADIENTS = [
  ["#10324a", "#2d5a7e"],
  ["#1f3d2e", "#3d6b52"],
  ["#2c1810", "#5c3820"],
  ["#0a1628", "#2d3e5a"],
  ["#3d2645", "#6b4a7d"],
  ["#4a3218", "#8a5a2b"],
  ["#8a1125", "#c23b54"],
];

const hashString = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const gradientFor = (name) =>
  AVATAR_GRADIENTS[hashString(name) % AVATAR_GRADIENTS.length];

const initialsFor = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "··";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar = ({ name, size = 64 }) => {
  const [a, b] = gradientFor(name);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "16px",
        background: `linear-gradient(135deg, ${a} 0%, ${b} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: `0 1px 0 rgba(255,255,255,0.4) inset, 0 14px 30px -14px ${a}66`,
        transition: "background 0.4s ease",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "16px",
          background:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.24), transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: size * 0.34,
          letterSpacing: "-0.02em",
          color: "#fff",
          position: "relative",
          zIndex: 1,
        }}
      >
        {initialsFor(name)}
      </Typography>
    </Box>
  );
};

const SectionLabel = ({ index, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
  </Typography>
);

const TextInput = ({ value, onChange, placeholder, autoFocus }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      px: 2,
      height: 48,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "12px",
      transition: "all 0.25s ease",
      "&:hover": { borderColor: "rgba(10,22,40,0.2)" },
      "&:focus-within": {
        borderColor: "rgba(10,22,40,0.32)",
        boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
      },
    }}
  >
    <InputBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      fullWidth
      sx={{
        fontSize: "15px",
        fontWeight: 500,
        fontFamily: '"DM Sans", sans-serif',
        color: INK,
        "& input::placeholder": { color: INK_MUTED, opacity: 1 },
      }}
    />
  </Box>
);

export const FacilityEditModal = ({ facilityId, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [edited, setEdited] = useState(false);
  const [facilityData, setFacilityData] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: facility, isLoading } = GetFacilityById(facilityId);

  useEffect(() => {
    if (facility?.data) {
      setFacilityData(facility.data);
    }
  }, [facility]);

  useEffect(() => {
    if (!confirmDelete) return undefined;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const deleteFacilityMutation = DeleteFacility({
    onSuccess: () => {
      enqueueSnackbar("Anläggning raderad.", { variant: "success" });
      onClose && onClose();
    },
    onError: (error) => {
      const errorCode = error?.response?.data?.code;
      if (errorCode === "FACILITY_HAS_GROUPS") {
        enqueueSnackbar(
          "Anläggningen kan inte tas bort eftersom den är kopplad till en eller flera grupper.",
          { variant: "error" }
        );
      } else {
        enqueueSnackbar("Kunde inte radera anläggning.", { variant: "error" });
      }
      setConfirmDelete(false);
    },
  });

  const updateFacilityMutation = UpdateFacility({
    onSuccess: () => {
      enqueueSnackbar("Anläggning uppdaterad.", { variant: "success" });
      onClose && onClose();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte uppdatera anläggning.", { variant: "error" });
    },
  });

  const handleDeleteClick = () => {
    if (confirmDelete) {
      deleteFacilityMutation.mutate(facilityId);
    } else {
      setConfirmDelete(true);
    }
  };

  const handleUpdate = () => {
    updateFacilityMutation.mutate({
      facilityId,
      updatedFacilityData: facilityData,
    });
    setEdited(false);
  };

  const name = facilityData?.facility_name || "";
  const saving = updateFacilityMutation.isLoading;
  const deleting = deleteFacilityMutation.isLoading;
  const canSave = edited && !saving && name.trim().length > 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          mb: 4,
          pb: 3,
          borderBottom: `1px solid ${LINE_SOFT}`,
        }}
      >
        <Avatar name={name || "?"} size={64} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "10.5px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
              mb: 0.5,
            }}
          >
            Anläggning
          </Typography>
          {isLoading && !name ? (
            <Skeleton width="65%" height={30} />
          ) : (
            <Typography
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: "1.625rem",
                fontWeight: 600,
                color: INK,
                letterSpacing: "-0.025em",
                lineHeight: 1.15,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name || "Namnlös anläggning"}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="01">Identitet</SectionLabel>
        <Stack spacing={2.25}>
          <Box>
            <FieldLabel>Namn på anläggning</FieldLabel>
            <TextInput
              value={facilityData?.facility_name || ""}
              placeholder="T.ex. Huvudkontor Stockholm"
              onChange={(e) => {
                setFacilityData({
                  ...facilityData,
                  facility_name: e.target.value,
                });
                setEdited(true);
              }}
            />
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={handleDeleteClick}
          disabled={deleting}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            background: confirmDelete ? BRAND : "transparent",
            color: confirmDelete ? "#fff" : BRAND,
            border: `1px solid ${
              confirmDelete ? BRAND : "rgba(138,17,37,0.26)"
            }`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: deleting ? "wait" : "pointer",
            transition: "all 0.25s ease",
            opacity: deleting ? 0.6 : 1,
            "&:hover": {
              background: confirmDelete ? "#6b0d1d" : "rgba(138,17,37,0.06)",
              borderColor: BRAND,
            },
          }}
        >
          {deleting ? (
            <CircularProgress size={14} sx={{ color: BRAND }} />
          ) : (
            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
          )}
          {confirmDelete ? "Klicka igen för att bekräfta" : "Ta bort anläggning"}
        </Box>

        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              px: 2.25,
              py: 1.25,
              background: "transparent",
              color: INK_MUTED,
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.25s ease",
              "&:hover": {
                color: INK,
                borderColor: "rgba(10,22,40,0.22)",
              },
            }}
          >
            Avbryt
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleUpdate}
            disabled={!canSave}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.75,
              py: 1.25,
              background: INK,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: canSave ? "pointer" : "not-allowed",
              opacity: canSave ? 1 : 0.4,
              transition: "all 0.25s ease",
              boxShadow: canSave
                ? "0 10px 24px -12px rgba(10,22,40,0.55)"
                : "none",
              "&:hover": canSave
                ? {
                    background: "#1a2638",
                    transform: "translateY(-1px)",
                  }
                : {},
            }}
          >
            {saving ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <CheckRoundedIcon sx={{ fontSize: 16 }} />
            )}
            Spara ändringar
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
